import { Formik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { LuDownload } from "react-icons/lu";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import CustomModal from "../../../components/CustomModal";
import FileUpload from "../../../components/FileUpload";
import Input from "../../../components/input";
import LogsComments from "../../../components/LogsComments";
import ConfirmDeleteModal from "../../../components/modals/ConfirmDeleteModal";
import NewTableComponent from "../../../components/NewTableComponent";
import Select from "../../../components/Select";
import { apiService } from "../../../services/apiService";
import { fetchBanksDropdown } from "../../../store/Slices/dropdownSlice";
import "../../../styles/Access.css";
import "../../../styles/DriverPayments.css";
import "../../../styles/DriverSafety.css";
import "../../../styles/Input.css";
import "../../../styles/Invoices.css";
import "../../../styles/Issue.css";
import "../../../styles/TripCostEstimation.css";
import { formats, modules } from "../../../utils/helper";
import SpinnerLoader from "../../../utils/SpinnerLoader";
import { errorNotify, successNotify } from "../../../utils/toast";
import { formatProvinceLabel } from "../../../utils/provinceOptions";
import { formatAmount } from "./Invoices";
const toTrimmedText = (value = "") => String(value ?? "").trim();
const formatLineFormula = (values = []) => values.filter(Boolean).join(" | ");
const formatExpenseLabel = (key = "") => {
  const labelLookup = {
    total_fuel_expense: "Fuel Expense",
    total_shortage_expense: "Shortage Expense",
    total_insurance_expense: "Insurance Expense",
    total_operational_expenses: "Operational Expense",
  };
  return labelLookup[key] || key;
};
const formatSettlementType = (type = "") =>
  String(type || "")
    .split("_")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");

const resolveApiFileUrl = (value = "") => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return "";
  if (/^(https?:|blob:|data:)/i.test(normalizedValue)) return normalizedValue;
  return `${process.env.REACT_APP_API_FILES_URL || ""}${normalizedValue}`;
};

const buildTripInvoiceInfoCalculation = (responseData = {}) => {
  const invoiceInfoData = responseData?.data || responseData || {};
  const trips = Array.isArray(invoiceInfoData?.trips)
    ? invoiceInfoData.trips
    : [];
  const lineItems = [];
  trips.forEach((tripItem, tripIndex) => {
    const tripCode = toTrimmedText(
      tripItem?.trip?.trip_id || tripItem?.trip_id,
    );
    const settlementDetails = Array.isArray(tripItem?.settlement_details)
      ? tripItem.settlement_details
      : [];
    settlementDetails.forEach((settlementDetail, detailIndex) => {
      lineItems.push({
        id:
          settlementDetail?.id ||
          `settlement-${tripIndex}-${detailIndex}-${settlementDetail?.type || "line"}`,
        title: toTrimmedText(settlementDetail?.name) || "Settlement Detail",
        formula: formatLineFormula([
          tripCode,
          formatSettlementType(settlementDetail?.type),
        ]),
        amount: toNumber(settlementDetail?.amount),
      });
    });
    [
      "total_fuel_expense",
      "total_shortage_expense",
      "total_insurance_expense",
      "total_operational_expenses",
    ].forEach((expenseKey) => {
      const expenseAmount = toNumber(tripItem?.[expenseKey]);
      if (!expenseAmount) {
        return;
      }
      lineItems.push({
        id: `${expenseKey}-${tripItem?.id || tripIndex}`,
        title: formatExpenseLabel(expenseKey),
        formula: formatLineFormula([tripCode, "Expense Deduction"]),
        amount: -expenseAmount,
      });
    });
  });
  return {
    lineItems,
    subtotal: toNumber(invoiceInfoData?.subtotal),
    salesTax: toNumber(invoiceInfoData?.tax),
    discount: toNumber(invoiceInfoData?.discount),
    totalAmount: toNumber(invoiceInfoData?.total_amount),
    rawData: invoiceInfoData,
  };
};

const DETAIL_TABS = [
  { key: "general_details", label: "General Details" },
  { key: "payment_history", label: "Payment History" },
];

const safeArray = (value) => (Array.isArray(value) ? value : []);
const toNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const renderFieldLabel = (label, required = false) => (
  <span className="assets_vehicle_field_label invoice_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const formatInputDate = (value = "") => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return "";
  return normalizedValue.split("T")[0] || "";
};

const formatStatusLabel = (value = "") =>
  String(value || "")
    .split("_")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");

const formatInvoiceTypeLabel = (value = "") => {
  const norm = String(value || "")
    .trim()
    .toLowerCase();
  if (norm === "monthly") return "Monthly";
  if (norm === "weekly") return "Weekly";
  if (norm === "ad_hoc") return "AdHoc";
  if (norm === "single") return "Single";
  return norm
    .split("_")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
};

const pickFirstText = (...values) => {
  for (const value of values) {
    const normalizedValue = String(value ?? "").trim();
    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return "";
};

const isObjectLike = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const pickFirstArray = (...values) => {
  for (const value of values) {
    if (Array.isArray(value)) {
      return value;
    }
  }

  return [];
};

const getInvoiceHistoryPrimaryComment = (item = {}) => {
  if (Array.isArray(item?.comments)) {
    const firstComment = item.comments.find((comment) => isObjectLike(comment));
    if (firstComment) {
      return firstComment;
    }

    const firstStringComment = item.comments.find(
      (comment) => typeof comment === "string",
    );
    if (typeof firstStringComment === "string") {
      return { comment: firstStringComment };
    }
  }

  if (isObjectLike(item?.comments)) {
    return item.comments;
  }

  return item;
};

const buildVehicleLabelLookup = (vehicles = []) =>
  safeArray(vehicles).reduce((lookup, vehicle) => {
    const vehicleId = String(
      vehicle?.value ??
        vehicle?.id ??
        vehicle?.vehicle_id ??
        vehicle?.ref_id ??
        "",
    ).trim();
    const vehicleLabel = pickFirstText(
      vehicle?.label,
      vehicle?.registration_number,
      vehicle?.registration_no,
      vehicle?.vehicle_registration_number,
      vehicle?.name,
      vehicle?.title,
      vehicle?.vehicle_id,
    );

    if (vehicleId && vehicleLabel) {
      lookup[vehicleId] = vehicleLabel;
    }

    return lookup;
  }, {});

const normalizeInvoiceHistoryAuthor = (...sources) => {
  const authorSource = sources.find((source) => isObjectLike(source)) || {};

  return {
    id:
      authorSource?.id ??
      authorSource?.user_id ??
      authorSource?.created_by ??
      authorSource?.created_by_user_id ??
      "",
    username: pickFirstText(
      authorSource?.username,
      authorSource?.name,
      authorSource?.full_name,
      authorSource?.email,
    ),
    image: pickFirstText(
      authorSource?.image,
      authorSource?.profile_image,
      authorSource?.avatar,
    ),
  };
};

const inferInvoiceHistoryItemType = (item = {}, fallbackType = "all") => {
  const sourceType = String(item?.source_type || item?.type || "")
    .trim()
    .toLowerCase();
  if (
    sourceType === "comment" ||
    sourceType === "log" ||
    sourceType === "activity"
  ) {
    return sourceType === "activity" ? "log" : sourceType;
  }

  const hasCommentShape =
    isObjectLike(item?.comments) ||
    typeof item?.comment !== "undefined" ||
    Array.isArray(item?.parent) ||
    Array.isArray(item?.replies) ||
    Array.isArray(item?.children) ||
    typeof item?.parent_id !== "undefined";

  if (hasCommentShape) {
    return "comment";
  }

  if (fallbackType === "comment") {
    return "comment";
  }

  return "log";
};

const normalizeInvoiceHistoryComment = (item = {}) => {
  const commentSource = getInvoiceHistoryPrimaryComment(item);
  const author = normalizeInvoiceHistoryAuthor(
    commentSource?.user,
    commentSource?.created_by_user,
    commentSource?.createdByUser,
    commentSource?.commented_by_user,
    commentSource?.comment_by_user,
    commentSource?.addresser,
    commentSource?.author,
    item?.user,
    item?.created_by_user,
    item?.createdByUser,
    item?.commented_by_user,
    item?.comment_by_user,
    item?.addresser,
    item?.author,
  );
  const replies = pickFirstArray(
    commentSource?.parent,
    commentSource?.replies,
    item?.parent,
    item?.replies,
    item?.children,
  ).map((reply) => normalizeInvoiceHistoryComment(reply));
  const commentText = pickFirstText(
    commentSource?.comment,
    item?.comment,
    typeof item?.comments === "string" ? item.comments : "",
    commentSource?.text,
    item?.text,
    commentSource?.message,
    item?.message,
    commentSource?.description,
    item?.description,
    commentSource?.body,
    item?.body,
  );
  const normalizedId =
    item?.id ??
    commentSource?.id ??
    item?.comment_id ??
    commentSource?.comment_id ??
    "";
  const normalizedCreatedAt =
    item?.created_at ||
    commentSource?.created_at ||
    item?.updated_at ||
    commentSource?.updated_at ||
    "";

  return {
    ...item,
    id: normalizedId,
    created_at: normalizedCreatedAt,
    source_type: "comment",
    type: "comment",
    comment: commentText,
    user: author,
    created_by_user: author,
    comments: {
      ...commentSource,
      id: commentSource?.id ?? normalizedId,
      comment: commentText,
      parent: replies,
      user: author,
      created_by_user: author,
    },
  };
};

const normalizeInvoiceHistoryLog = (item = {}) => {
  const logSource = isObjectLike(item?.logs) ? item.logs : item;
  const beforeText = pickFirstText(logSource?.before, item?.before);
  const afterText = pickFirstText(
    logSource?.after,
    item?.after,
    logSource?.message,
    item?.message,
    logSource?.description,
    item?.description,
    logSource?.activity,
    item?.activity,
    logSource?.title,
    item?.title,
  );
  const normalizedId =
    item?.id ?? logSource?.id ?? item?.log_id ?? logSource?.log_id ?? "";
  const normalizedCreatedAt =
    item?.created_at ||
    logSource?.created_at ||
    item?.updated_at ||
    logSource?.updated_at ||
    "";

  return {
    ...item,
    id: normalizedId,
    created_at: normalizedCreatedAt,
    source_type: "log",
    type: "log",
    logs: {
      ...logSource,
      before: beforeText,
      after: afterText,
    },
  };
};

const extractInvoiceHistoryItems = (response = {}) => {
  const responseData = response?.data || response || {};

  if (Array.isArray(responseData?.results)) return responseData.results;
  if (Array.isArray(responseData?.history)) return responseData.history;
  if (
    Array.isArray(responseData?.comments) ||
    Array.isArray(responseData?.logs)
  ) {
    return [
      ...safeArray(responseData.comments),
      ...safeArray(responseData.logs),
    ];
  }
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.history)) return response.history;
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(response)) return response;
  return [];
};

const getNormalizedInvoiceCommentId = (item = {}) =>
  String(
    item?.comments?.id ??
      item?.id ??
      item?.comment_id ??
      item?.comments?.comment_id ??
      "",
  ).trim();

const getNormalizedInvoiceCommentParentId = (item = {}) =>
  String(
    item?.comments?.parent_id ??
      item?.parent_id ??
      item?.comments?.reply_to_id ??
      item?.reply_to_id ??
      "",
  ).trim();

const normalizeInvoiceHistoryItems = (items = [], historyType = "all") => {
  const normalizedItems = safeArray(items).map((item) => {
    const resolvedType = inferInvoiceHistoryItemType(item, historyType);
    return resolvedType === "comment"
      ? normalizeInvoiceHistoryComment(item)
      : normalizeInvoiceHistoryLog(item);
  });

  const commentLookup = new Map();
  normalizedItems.forEach((item) => {
    if (item?.type !== "comment") return;
    const commentId = getNormalizedInvoiceCommentId(item);
    if (commentId) {
      commentLookup.set(commentId, item);
    }
  });

  const topLevelItems = [];

  normalizedItems.forEach((item) => {
    if (item?.type !== "comment") {
      topLevelItems.push(item);
      return;
    }

    const commentId = getNormalizedInvoiceCommentId(item);
    const parentId = getNormalizedInvoiceCommentParentId(item);
    const hasParent = parentId && parentId !== "0" && parentId !== commentId;

    if (!hasParent) {
      topLevelItems.push(item);
      return;
    }

    const parentComment = commentLookup.get(parentId);

    if (!parentComment) {
      topLevelItems.push(item);
      return;
    }

    const existingReplies = safeArray(parentComment?.comments?.parent);
    const alreadyAttached = existingReplies.some(
      (reply) => getNormalizedInvoiceCommentId(reply) === commentId,
    );

    if (!alreadyAttached) {
      parentComment.comments = {
        ...(parentComment.comments || {}),
        parent: [...existingReplies, item],
      };
    }
  });

  return topLevelItems;
};

const buildComponentTotalRows = ({
  subtotal = 0,
  salesTax = 0,
  discount = 0,
  total = 0,
}) => [
  {
    id: "subtotal",
    label: "Subtotal",
    formula: "",
    amount: subtotal,
  },
  {
    id: "sales-tax",
    label: "Sales Tax",
    formula: "",
    amount: salesTax,
  },
  {
    id: "discount",
    label: "Discount",
    formula: "",
    amount: discount,
  },
  {
    id: "total",
    label: "Total Amount (PKR)",
    formula: "",
    amount: total,
    isTotal: true,
  },
];

const buildInvoiceCalculationView = (invoiceData = {}) =>
  buildTripInvoiceInfoCalculation({
    data: {
      trips: safeArray(invoiceData?.settlements),
      subtotal: invoiceData?.subtotal,
      discount: invoiceData?.discount,
      tax: invoiceData?.tax,
      total_amount: invoiceData?.total_amount,
    },
  });

const buildSingleCalculationCards = (invoiceData = {}) => {
  const calculation = buildInvoiceCalculationView(invoiceData);
  const detailRows = calculation.lineItems.length
    ? calculation.lineItems.map((item) => ({
        id: item.id,
        label: item.title,
        formula: item.formula,
        amount: item.amount,
      }))
    : [
        {
          id: "invoice-subtotal",
          label: "Saved Invoice Subtotal",
          formula: "",
          amount: calculation.subtotal,
        },
      ];

  return {
    calculation,
    cards: [
      {
        id: "invoice-summary",
        title: "Invoice Summary",
        subtitle: "Saved totals for this single trip invoice",
        amount: calculation.totalAmount,
        detailRows,
        totalRows: buildComponentTotalRows(calculation),
      },
    ],
  };
};

const buildTripRouteLabel = (settlement = {}) => {
  const tripStatus = formatStatusLabel(settlement?.trip?.status || "");
  if (tripStatus) {
    return `Status: ${tripStatus}`;
  }

  if (settlement?.trip?.distance) {
    return `Distance: ${settlement.trip.distance} km`;
  }

  return "Trip Settlement";
};

const getRouteStopLabel = (stop = {}) =>
  pickFirstText(
    stop?.province,
    stop?.location,
    stop?.name,
    stop?.title,
    stop?.id ? `Stop #${stop.id}` : "",
  );

const formatRouteText = (value = "") => {
  const norm = String(value || "").trim();
  if (!norm) return "";
  return norm.replace(/\s*->\s*/g, " → ");
};

const buildSettlementRouteLegs = (settlement = {}) => {
  const legs = safeArray(settlement?.trips_legs);

  if (!legs.length) {
    const fallbackRoute = pickFirstText(
      settlement?.trip?.route,
      settlement?.route,
    );
    return fallbackRoute
      ? [{ id: "route", label: formatRouteText(fallbackRoute) }]
      : [];
  }

  return legs.map((leg, index) => {
    const sourceLabel = getRouteStopLabel(leg?.source_stop);
    const destinationLabel = getRouteStopLabel(leg?.destination_stop);

    return {
      id:
        leg?.id ||
        `${leg?.source_stop_id || "source"}-${leg?.destination_stop_id || "destination"}-${index}`,
      label:
        sourceLabel && destinationLabel
          ? `${sourceLabel} → ${destinationLabel}`
          : sourceLabel || destinationLabel || `Leg ${index + 1}`,
    };
  });
};

const buildBulkTripCards = (invoiceData = {}, vehicleLabelLookup = {}) => {
  const settlements = safeArray(invoiceData?.settlements);
  const calculation = buildInvoiceCalculationView(invoiceData);

  return {
    calculation,
    tripCards: settlements.map((settlement, index) => {
      const tripCode =
        settlement?.trip?.trip_id || settlement?.trip_id || `Trip ${index + 1}`;
      const tripProfit = toNumber(settlement?.trip_net_profit);
      const tripCalculation = buildTripInvoiceInfoCalculation({
        data: {
          trips: [settlement],
          subtotal: settlement?.trip_net_profit,
          discount: 0,
          tax: 0,
          total_amount: settlement?.trip_net_profit,
        },
      });

      return {
        id: String(settlement?.id ?? tripCode),
        title: tripCode,
        routeLabel: buildTripRouteLabel(settlement),
        amount: tripProfit,
        components: [
          {
            id: `${tripCode}-settlement-breakdown`,
            title: "Settlement Breakdown",
            subtitle: `Vehicle: ${pickFirstText(
              settlement?.trip?.vehicle?.registration_number,
              settlement?.trip?.vehicle?.registration_no,
              settlement?.trip?.vehicle?.name,
              settlement?.vehicle?.registration_number,
              settlement?.vehicle?.registration_no,
              settlement?.vehicle?.name,
              vehicleLabelLookup[String(settlement?.vehicle_id ?? "").trim()],
              settlement?.vehicle_id ? `Vehicle ${settlement.vehicle_id}` : "-",
            )}`,
            amount: tripProfit,
            detailRows: tripCalculation.lineItems.map((item) => ({
              id: item.id,
              label: item.title,
              formula: item.formula,
              amount: item.amount,
            })),
            totalRows: buildComponentTotalRows({
              subtotal: tripProfit,
              salesTax: 0,
              discount: 0,
              total: tripProfit,
            }),
          },
        ],
      };
    }),
  };
};

const InvoiceDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();

  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recordNotFound, setRecordNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState("general_details");
  const [paymentsData, setPaymentsData] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
  const [transactionSaving, setTransactionSaving] = useState(false);
  const [isEditingPaymentTransaction, setIsEditingPaymentTransaction] =
    useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showDeleteTransactionModal, setShowDeleteTransactionModal] =
    useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [vehicleLabelLookup, setVehicleLabelLookup] = useState({});

  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState(""); // "approve", "reject"
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleOpenApprovalModal = (action) => {
    setApprovalAction(action);
    setApprovalRemarks("");
    setShowApprovalModal(true);
  };

  const handleApprovalActionSubmit = async () => {
    setApprovalSubmitting(true);
    const apiStatus = approvalAction === "approve" ? "approved" : "rejected";
    try {
      const payload = {
        invoice_id: Number(id),
        status: apiStatus,
        comments: approvalRemarks,
      };
      await apiService.approveRejectTripInvoice(payload);
      successNotify(`Invoice ${apiStatus} successfully`);
      setShowApprovalModal(false);
      await fetchInvoiceDetail();
      await handleFetchInvoiceHistory("all");
    } catch (error) {
      errorNotify(error || `Failed to ${approvalAction} invoice`);
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const fetchInvoiceDetail = useCallback(async () => {
    if (!id) {
      setInvoiceData(null);
      setRecordNotFound(true);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.getTripInvoiceDetail(id);
      const nextInvoiceData = response?.data || response || null;
      setInvoiceData(nextInvoiceData);
      setRecordNotFound(!nextInvoiceData);
    } catch (error) {
      setInvoiceData(null);
      setRecordNotFound(true);
      errorNotify(error || "Failed to fetch invoice details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchInvoicePayments = useCallback(async () => {
    if (!id) return;
    setPaymentsLoading(true);
    try {
      const response = await apiService.getTripInvoicePayments(id);
      const nextPayments = response?.data || response || [];
      setPaymentsData(Array.isArray(nextPayments) ? nextPayments : []);
    } catch (error) {
      errorNotify(error || "Failed to fetch invoice payments");
      setPaymentsData([]);
    } finally {
      setPaymentsLoading(false);
    }
  }, [id]);

  const handleDownloadInvoice = async () => {
    if (!id || downloading) return;

    setDownloading(true);

    try {
      const response = await apiService.exportTripInvoicePdf(id);
      const filePath =
        response?.data?.file_path ||
        response?.data?.filePath ||
        response?.data?.url ||
        response?.data ||
        response?.file_path ||
        response?.filePath ||
        response?.url ||
        "";
      const fileUrl = resolveApiFileUrl(filePath);

      if (!fileUrl) {
        errorNotify("Invoice PDF not found");
        return;
      }

      const anchor = document.createElement("a");
      anchor.href = fileUrl;
      anchor.target = "_blank";
      anchor.download = `${overviewValues.invoiceId || "invoice"}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      successNotify("Invoice downloaded successfully");
    } catch (error) {
      errorNotify(error || "Failed to download invoice PDF");
    } finally {
      setDownloading(false);
    }
  };

  const paymentMethodOptions = [
    { label: "Cash", value: "cash" },
    { label: "Bank Transfer", value: "bank_transfer" },
    { label: "Cheque", value: "cheque" },
  ];

  const banksDropdownData = useSelector(
    (state) => state.dropdowns?.banksDropdownData || [],
  );

  const paymentTransactionModalInitialValues = useMemo(() => {
    if (isEditingPaymentTransaction && editingTransaction) {
      return {
        amount: editingTransaction.amount || "",
        payment_method: editingTransaction.payment_method || "",
        reference_number:
          editingTransaction.reference_number || editingTransaction.refNo || "",
        notes: editingTransaction.notes || "",
        bank_name: editingTransaction.bank_name || "",
        account_title: editingTransaction.account_title || "",
        iban_number: editingTransaction.iban_number || "",
        fileObjects: editingTransaction.fileObjects || [],
      };
    }
    return {
      amount: "",
      payment_method: "",
      reference_number: "",
      notes: "",
      bank_name: "",
      account_title: "",
      iban_number: "",
      fileObjects: [],
    };
  }, [isEditingPaymentTransaction, editingTransaction]);

  const handleOpenAddTransactionModal = () => {
    setIsEditingPaymentTransaction(false);
    setEditingTransaction(null);
    setShowAddTransactionModal(true);
  };

  const handleOpenEditTransactionModal = (transaction) => {
    setIsEditingPaymentTransaction(true);
    setEditingTransaction(transaction);
    setShowAddTransactionModal(true);
  };

  const handleOpenDeleteTransactionModal = (transaction) => {
    setTransactionToDelete(transaction);
    setShowDeleteTransactionModal(true);
  };

  const handleAddPaymentTransaction = async (
    values,
    { setSubmitting, resetForm },
  ) => {
    setTransactionSaving(true);
    try {
      const payload = {
        invoice_id: Number(id),
        amount: Number(values.amount) || 0,
        payment_method: values.payment_method || "cash",
        reference_number: values.reference_number || "",
        notes: values.notes || "",
        bank_name:
          values.payment_method === "bank_transfer" ? values.bank_name : "",
        account_title:
          values.payment_method === "bank_transfer" ? values.account_title : "",
        iban_number:
          values.payment_method === "bank_transfer" ? values.iban_number : "",
        files: (values.fileObjects || [])
          .map((f) => f.file_path || f.url || f.fileName || f.name || "")
          .filter(Boolean),
      };

      await apiService.createInvoicePayment(payload);
      successNotify("Payment transaction recorded successfully");
      setShowAddTransactionModal(false);
      resetForm();
      await fetchInvoiceDetail();
      await fetchInvoicePayments();
    } catch (error) {
      errorNotify(error || "Failed to record payment transaction");
    } finally {
      setTransactionSaving(false);
      setSubmitting(false);
    }
  };

  const handleEditPaymentTransaction = async (
    values,
    { setSubmitting, resetForm },
  ) => {
    if (!editingTransaction?.id) {
      errorNotify("Transaction ID is missing");
      return;
    }
    setTransactionSaving(true);
    try {
      const payload = {
        invoice_id: Number(id),
        amount: Number(values.amount) || 0,
        payment_method: values.payment_method || "cash",
        reference_number: values.reference_number || "",
        notes: values.notes || "",
        bank_name:
          values.payment_method === "bank_transfer" ? values.bank_name : "",
        account_title:
          values.payment_method === "bank_transfer" ? values.account_title : "",
        iban_number:
          values.payment_method === "bank_transfer" ? values.iban_number : "",
        files: (values.fileObjects || [])
          .map((f) => f.file_path || f.url || f.fileName || f.name || "")
          .filter(Boolean),
      };

      await apiService.updateInvoicePayment(editingTransaction.id, payload);
      successNotify("Payment transaction updated successfully");
      setShowAddTransactionModal(false);
      resetForm();
      await fetchInvoiceDetail();
      await fetchInvoicePayments();
    } catch (error) {
      errorNotify(error || "Failed to update payment transaction");
    } finally {
      setTransactionSaving(false);
      setSubmitting(false);
    }
  };

  const handleDeletePaymentTransaction = async () => {
    if (!transactionToDelete?.id) return;
    setTransactionSaving(true);
    try {
      await apiService.deleteInvoicePayment(transactionToDelete.id);
      successNotify("Payment transaction deleted successfully");
      setShowDeleteTransactionModal(false);
      setTransactionToDelete(null);
      await fetchInvoiceDetail();
      await fetchInvoicePayments();
    } catch (error) {
      errorNotify(error || "Failed to delete payment transaction");
    } finally {
      setTransactionSaving(false);
    }
  };

  const handleFetchInvoiceHistory = useCallback(
    async (historyType = "all") => {
      if (!id) return;

      setHistoryLoading(true);

      try {
        const response = await apiService.getTripInvoiceHistory({
          trips_invoice_id: id,
          history_type: historyType,
        });
        const nextHistoryItems = normalizeInvoiceHistoryItems(
          extractInvoiceHistoryItems(response),
          historyType,
        );

        setHistoryItems(nextHistoryItems);
        return nextHistoryItems;
      } catch (error) {
        errorNotify(error || "Failed to fetch invoice history");
        setHistoryItems([]);
        return [];
      } finally {
        setHistoryLoading(false);
      }
    },
    [id],
  );

  const handleSubmitInvoiceComment = useCallback(
    async ({ comment, parent_id, mention_user_ids }) => {
      if (!id) return;

      setCommentSubmitting(true);

      try {
        await apiService.commentOnTripInvoice({
          trips_invoice_id: Number(id) || id,
          comment,
          parent_id,
          mention_user_ids,
        });
        await handleFetchInvoiceHistory("all");
      } catch (error) {
        errorNotify(error || "Failed to send invoice comment");
        throw error;
      } finally {
        setCommentSubmitting(false);
      }
    },
    [id, handleFetchInvoiceHistory],
  );

  useEffect(() => {
    fetchInvoiceDetail();
    fetchInvoicePayments();
  }, [fetchInvoiceDetail, fetchInvoicePayments]);

  useEffect(() => {
    dispatch(fetchBanksDropdown());
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      handleFetchInvoiceHistory("all");
    }
  }, [id, handleFetchInvoiceHistory]);

  useEffect(() => {
    let isMounted = true;

    const fetchVehicleLabels = async () => {
      try {
        const response = await apiService.getVehiclesDropdown();
        const dropdownData = response?.data || response || [];

        if (!isMounted) {
          return;
        }

        setVehicleLabelLookup(buildVehicleLabelLookup(dropdownData));
      } catch (error) {
        if (isMounted) {
          setVehicleLabelLookup({});
        }
      }
    };

    fetchVehicleLabels();

    return () => {
      isMounted = false;
    };
  }, []);

  const isBulkInvoice =
    String(invoiceData?.invoice_type || "")
      .trim()
      .toLowerCase() === "multiple";

  const formatTableDate = (value = "") => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) return "-";
    const datePart = normalizedValue.split("T")[0] || "";
    const [year, month, day] = datePart.split("-");
    if (!year || !month || !day) return normalizedValue;
    return `${year}-${day}-${month}`;
  };

  const formatTableDateTime = (value = "") => {
    if (value === null || value === undefined) return "-";
    const normalizedValue = String(value).trim();
    if (
      !normalizedValue ||
      normalizedValue === "null" ||
      normalizedValue === "undefined"
    )
      return "-";
    if (normalizedValue.includes("T")) {
      const [datePart, timePart] = normalizedValue.split("T");
      const [year, month, day] = datePart.split("-");
      const timeClean = (timePart || "").split(".")[0] || "";
      if (!year || !month || !day) return normalizedValue;
      return `${year}-${day}-${month} ${timeClean}`;
    }
    return normalizedValue;
  };

  const overviewValues = useMemo(
    () => ({
      invoiceId: invoiceData?.trips_invoice_id || "-",
      customerName: invoiceData?.customer?.name || "-",
      contractName:
        invoiceData?.contract?.name ||
        (invoiceData?.contract?.contracts_template_id
          ? `Contract #${invoiceData.contract.contracts_template_id}`
          : invoiceData?.contract_id
            ? `Contract #${invoiceData.contract_id}`
            : "-"),
      invoiceType: formatInvoiceTypeLabel(invoiceData?.invoice_type),
      status: formatStatusLabel(invoiceData?.status) || "-",
      invoiceDate: formatInputDate(
        invoiceData?.invoice_at || invoiceData?.created_at,
      ),
      dueDate: formatInputDate(
        invoiceData?.invoice_due_at || invoiceData?.billing_period_to,
      ),
      createdBy: invoiceData?.created_by_user?.username || "-",
      notes: invoiceData?.notes || "",
    }),
    [invoiceData],
  );

  // 1. Trips & Calculation Mapper
  const tripsCalculationRows = useMemo(() => {
    return safeArray(invoiceData?.settlements).flatMap((settlement, index) => {
      const tripCode =
        settlement?.trip?.trip_id || settlement?.trip_id || `Trip ${index + 1}`;
      const vehicleNo = pickFirstText(
        settlement?.trip?.vehicle?.registration_number,
        settlement?.trip?.vehicle?.registration_no,
        settlement?.trip?.vehicle?.name,
        settlement?.trip?.vehicle_type?.title,
        settlement?.vehicle_type?.title,
        settlement?.vehicle?.registration_number,
        settlement?.vehicle?.registration_no,
        settlement?.vehicle?.name,
        vehicleLabelLookup[String(settlement?.vehicle_id ?? "").trim()],
        "-",
      );
      const routeLegs = buildSettlementRouteLegs(settlement);
      const settlementDeduction = toNumber(
        settlement?.deduction_amount ??
          settlement?.invoice_deduction_amount ??
          toNumber(settlement?.total_fuel_expense) +
            toNumber(settlement?.total_shortage_expense) +
            toNumber(settlement?.total_insurance_expense) +
            toNumber(settlement?.total_operational_expenses),
        0,
      );
      const settlementFreight = toNumber(
        settlement?.invoice_gross_amount ??
          settlement?.freight_amount ??
          settlement?.base_freight,
        0,
      );
      const settlementTax = toNumber(
        settlement?.invoice_tax_amount ??
          settlement?.tax_amount ??
          settlement?.tax,
        0,
      );
      const settlementNet = toNumber(
        settlement?.invoice_net_amount ??
          settlement?.net_amount ??
          settlement?.total_amount,
        0,
      );
      const rawLegs = safeArray(settlement?.trips_legs);

      if (!routeLegs.length) {
        return [
          {
            id: settlement.id || String(index),
            tripCode,
            vehicleNo,
            route: "-",
            freight: settlementFreight,
            deduction: settlementDeduction,
            tax: settlementTax,
            net: settlementNet,
          },
        ];
      }

      return routeLegs.map((routeLeg, legIndex) => {
        const rawLeg = rawLegs[legIndex] || {};
        const freight = toNumber(
          rawLeg?.freight_amount,
          legIndex === 0 ? settlementFreight : 0,
        );
        const tax = toNumber(
          rawLeg?.tax_amount,
          legIndex === 0 ? settlementTax : 0,
        );

        return {
          id: `${settlement?.id || index}-${routeLeg.id || legIndex}`,
          tripCode: legIndex === 0 ? tripCode : "-",
          vehicleNo,
          route: routeLeg.label,
          freight,
          deduction: settlementDeduction,
          tax,
          net: settlementNet,
        };
      });
    });
  }, [invoiceData, vehicleLabelLookup]);


  // 3. Approval History Mapper
  const approvalHistoryRows = useMemo(() => {
    const approvals = safeArray(
      invoiceData?.approvals ?? invoiceData?.approval_history,
    );
    if (approvals.length > 0) {
      return approvals.map((app, index) => ({
        id: app.id || String(index),
        step:
          app.step_name ||
          app.step ||
          app.approval_role?.name ||
          `Stage ${app.stage || index + 1}`,
        actionBy:
          app.action_by_user?.username ||
          (app.action_by ? `User #${app.action_by}` : "-"),
        status: formatStatusLabel(app.status) || "Pending",
        dateTime: formatTableDateTime(
          app.action_at ||
            app.created_at ||
            app.updated_at ||
            app.action_by_user?.created_at ||
            app.action_by_user?.updated_at,
        ),
        comments:
          app.notes ||
          app.comments ||
          `Approval role: ${app.approval_role?.name || "-"}`,
      }));
    }

    const currentStatus = String(invoiceData?.status || "").toLowerCase();
    const steps = [];

    steps.push({
      id: "1",
      step: "Invoice Creation",
      actionBy: invoiceData?.created_by_user?.username || "Finance User",
      status: "Submitted",
      dateTime: formatTableDateTime(invoiceData?.created_at),
      comments: "Invoice generated successfully.",
    });

    if (
      currentStatus === "pending" ||
      currentStatus === "submitted" ||
      currentStatus === "paid"
    ) {
      steps.push({
        id: "2",
        step: "Finance Review",
        actionBy: "AJCL Admin",
        status: currentStatus === "pending" ? "Pending" : "Approved",
        dateTime:
          currentStatus === "pending"
            ? "-"
            : formatTableDateTime(invoiceData?.updated_at),
        comments:
          currentStatus === "pending"
            ? "Awaiting finance verification."
            : "Verified and approved.",
      });
    }

    if (currentStatus === "paid") {
      steps.push({
        id: "3",
        step: "Payment Clearance",
        actionBy: "Finance Treasury",
        status: "Paid",
        dateTime: formatTableDateTime(invoiceData?.updated_at),
        comments: "Payment received and recorded.",
      });
    } else if (currentStatus === "rejected") {
      steps.push({
        id: "2",
        step: "Finance Review",
        actionBy: "AJCL Admin",
        status: "Rejected",
        dateTime: formatTableDateTime(invoiceData?.updated_at),
        comments:
          invoiceData?.notes || "Rejected due to incorrect documentation.",
      });
    }

    return steps;
  }, [invoiceData]);

  // 4. Payment History Mapper
  const paymentHistoryRows = useMemo(() => {
    const payments = safeArray(paymentsData);
    if (payments.length > 0) {
      return payments.map((p) => ({
        id: p.id,
        refNo: p.reference_number || "-",
        method: String(p.payment_method || "-").toUpperCase(),
        payment_method: String(p.payment_method || "").toLowerCase(),
        amount: toNumber(p.paid_amount ?? p.amount, 0),
        date: formatTableDate(p.payment_date || p.created_at),
        notes: p.notes || "",
        bank_name: p.bank_name || "",
        account_title: p.account_title || "",
        iban_number: p.iban_number || "",
        fileObjects: Array.isArray(p.payment_files)
          ? p.payment_files.map((file) => ({
              name: file.file_path || file.fileName || "",
              file_path: file.file_path || "",
            }))
          : [],
      }));
    }

    if (String(invoiceData?.status || "").toLowerCase() === "paid") {
      return [
        {
          id: "1",
          refNo: "REF-12345",
          method: "CASH",
          payment_method: "cash",
          amount: toNumber(
            invoiceData?.net_amount ?? invoiceData?.total_amount,
            0,
          ),
          date: formatTableDate(invoiceData?.updated_at),
          notes: "Automatic payment mapping",
          bank_name: "",
          account_title: "",
          iban_number: "",
          fileObjects: [],
        },
      ];
    }

    return [];
  }, [paymentsData, invoiceData]);

  // 5. Audit Log Mapper
  const auditLogRows = useMemo(() => {
    const logs = safeArray(historyItems).filter(
      (item) => item.type === "log" || item.source_type === "log",
    );
    return logs.map((log, index) => ({
      id: log.id || String(index),
      activity: log.logs?.activity || log.activity || "Invoice Updated",
      doneBy:
        log.logs?.user?.username ||
        log.logs?.created_by_user?.username ||
        "System",
      dateTime: formatTableDateTime(log.created_at || log.updated_at),
      description: log.logs?.after || log.logs?.description || "-",
    }));
  }, [historyItems]);

  // Columns Definitions
  const tripsCalculationColumns = [
    {
      name: "Trip ID",
      selector: (row) => row.tripCode,
      tableColumn: false,
      minWidth: "120px",
    },
    {
      name: "Vehicle",
      selector: (row) => row.vehicleNo,
      tableColumn: false,
      minWidth: "140px",
    },
    {
      name: "Route",
      selector: (row) => row.route || "-",
      tableColumn: false,
      minWidth: "180px",
      wrap: true,
    },
    {
      name: "Freight (PKR)",
      selector: (row) => formatAmount(row.freight),
      tableColumn: false,
      minWidth: "140px",
    },
    {
      name: "Deduction (PKR)",
      selector: (row) => formatAmount(row.deduction),
      tableColumn: false,
      minWidth: "140px",
    },
    {
      name: "Tax (PKR)",
      selector: (row) => formatAmount(row.tax),
      tableColumn: false,
      minWidth: "120px",
    },
    {
      name: "Net Amount (PKR)",
      selector: (row) => formatAmount(row.net),
      tableColumn: false,
      minWidth: "160px",
    },
  ];

  const rentalBreakdownRows = safeArray(invoiceData?.rentals).map(
    (rental, index) => ({
      id: rental?.id || String(index),
      vehicleType: rental?.vehicle_type?.title || "-",
      noOfVehicles: rental?.no_of_vehicle ?? "-",
      monthlyRental: toNumber(rental?.monthly_rental),
      gross: toNumber(rental?.gross_amount),
      taxRate: toNumber(rental?.tax_percentage),
      tax: toNumber(rental?.tax_amount),
      net: toNumber(rental?.net_amount),
    }),
  );

  const rentalBreakdownColumns = [
    {
      name: "Vehicle Type",
      selector: (row) => row.vehicleType,
      tableColumn: false,
      minWidth: "160px",
    },
    {
      name: "No. of Vehicles",
      selector: (row) => row.noOfVehicles,
      tableColumn: false,
      minWidth: "140px",
    },
    {
      name: "Monthly Rental (PKR)",
      selector: (row) => formatAmount(row.monthlyRental),
      tableColumn: false,
      minWidth: "170px",
    },
    {
      name: "Gross Amount (PKR)",
      selector: (row) => formatAmount(row.gross),
      tableColumn: false,
      minWidth: "160px",
    },
    {
      name: "Tax Rate (%)",
      selector: (row) => `${row.taxRate}%`,
      tableColumn: false,
      minWidth: "120px",
    },
    {
      name: "Tax Amount (PKR)",
      selector: (row) => formatAmount(row.tax),
      tableColumn: false,
      minWidth: "160px",
    },
    {
      name: "Net Amount (PKR)",
      selector: (row) => formatAmount(row.net),
      tableColumn: false,
      minWidth: "160px",
    },
  ];

  const taxBreakdownColumns = [
    {
      name: "Trip ID",
      selector: (row) => row.tripCode,
      tableColumn: false,
      minWidth: "120px",
    },
    {
      name: "Vehicle",
      selector: (row) => row.vehicleNo,
      tableColumn: false,
      minWidth: "140px",
    },
    {
      name: "Source Province",
      selector: (row) => row.sourceProvince,
      tableColumn: false,
      minWidth: "150px",
    },
    {
      name: "Destination Province",
      selector: (row) => row.destinationProvince,
      tableColumn: false,
      minWidth: "180px",
    },
    {
      name: "Tax Rule",
      selector: (row) => row.taxRule,
      tableColumn: false,
      minWidth: "150px",
    },
    {
      name: "Base Freight (PKR)",
      selector: (row) => formatAmount(row.freight),
      tableColumn: false,
      minWidth: "180px",
    },
    {
      name: "Tax Rate (%)",
      selector: (row) => `${row.taxRate}%`,
      tableColumn: false,
      minWidth: "130px",
    },
    {
      name: "Tax Amount (PKR)",
      selector: (row) => formatAmount(row.taxAmount),
      tableColumn: false,
      minWidth: "180px",
    },
  ];

  const approvalHistoryColumns = [
    {
      name: "Step / Role",
      selector: (row) => row.step,
      tableColumn: false,
      minWidth: "160px",
    },
    {
      name: "Action By",
      selector: (row) => row.actionBy,
      tableColumn: false,
      minWidth: "160px",
    },
    {
      name: "Status",
      selector: (row) => {
        const isApproved =
          String(row.status || "").toLowerCase() === "approved" ||
          String(row.status || "").toLowerCase() === "submitted" ||
          String(row.status || "").toLowerCase() === "paid";
        const isRejected =
          String(row.status || "").toLowerCase() === "rejected";
        const className = isApproved
          ? "is_paid"
          : isRejected
            ? "is_rejected"
            : "is_new";
        return (
          <span className={`invoice_status ${className}`}>
            <span>{row.status}</span>
          </span>
        );
      },
      tableColumn: false,
      minWidth: "140px",
    },
    {
      name: "Date & Time",
      selector: (row) => row.dateTime,
      tableColumn: false,
      minWidth: "180px",
    },
    {
      name: "Comments",
      selector: (row) => row.comments,
      tableColumn: false,
      minWidth: "220px",
    },
  ];

  const paymentHistoryColumns = [
    {
      name: "Transaction ID",
      selector: (row) => (
        <span
          style={{
            color: "#4F19E2",
            textDecoration: "underline",
            cursor: "pointer",
            fontWeight: 500,
          }}
          onClick={() => handleOpenEditTransactionModal(row)}
        >
          {`TR-${row.id}`}
        </span>
      ),
      tableColumn: false,
      minWidth: "140px",
    },
    {
      name: "Amount Paid (PKR)",
      selector: (row) => formatAmount(row.amount),
      tableColumn: false,
      minWidth: "150px",
    },
    {
      name: "Payment Method",
      selector: (row) => {
        const methodStr = String(row.method || "").toLowerCase();
        if (methodStr === "bank_transfer") return "Bank Transfer";
        return methodStr.charAt(0).toUpperCase() + methodStr.slice(1);
      },
      tableColumn: false,
      minWidth: "150px",
    },
    {
      name: "Reference Number",
      selector: (row) => row.refNo || "-",
      tableColumn: false,
      minWidth: "160px",
    },
    {
      name: "Action",
      selector: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <FiEdit
            size={16}
            style={{ cursor: "pointer", color: "#1A4FBA" }}
            onClick={() => handleOpenEditTransactionModal(row)}
          />
          <FiTrash2
            size={16}
            style={{ cursor: "pointer", color: "#DC2626" }}
            onClick={() => handleOpenDeleteTransactionModal(row)}
          />
        </div>
      ),
      tableColumn: false,
      minWidth: "100px",
    },
  ];

  const handleExport = () => {
    if (!invoiceData) return;

    const exportLines = [
      `Invoice ID: ${overviewValues.invoiceId}`,
      `Customer: ${overviewValues.customerName}`,
      `Contract: ${overviewValues.contractName}`,
      `Type: ${overviewValues.invoiceType}`,
      `Status: ${overviewValues.status}`,
      `Gross Amount: ${formatAmount(invoiceData?.gross_amount)}`,
      `Deduction Amount: ${formatAmount(invoiceData?.deduction_amount)}`,
      `Tax Amount: ${formatAmount(invoiceData?.tax_amount)}`,
      `Net Amount: ${formatAmount(invoiceData?.net_amount)}`,
    ];

    const blob = new Blob([exportLines.join("\n")], {
      type: "text/plain;charset=utf-8",
    });
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = `${overviewValues.invoiceId || "invoice-details"}.txt`;
    anchor.click();
    window.URL.revokeObjectURL(objectUrl);
  };

  if (loading) {
    return <SpinnerLoader />;
  }

  if (recordNotFound || !invoiceData) {
    return (
      <div className="trip_cost_estimation_page invoice_details_page">
        <Breadcrumbs values={["Finance", "Invoices", "Invoice Details"]} />
        <div className="page_container">
          <div className="table_no_records">Invoice record not found</div>
        </div>
      </div>
    );
  }

  const totalAmountVal = toNumber(
    invoiceData?.overall_net_amount ??
      invoiceData?.net_amount ??
      invoiceData?.total_amount,
  );
  const outstandingVal = toNumber(
    invoiceData?.outstanding_amount ??
      (invoiceData?.status === "paid" ? 0 : totalAmountVal),
  );
  const receivedVal = toNumber(
    invoiceData?.paid_amount ??
      invoiceData?.received_amount ??
      (invoiceData?.status === "paid" ? totalAmountVal : 0),
  );
  const withheldVal = toNumber(invoiceData?.withheld_amount, 0);

  const isApproved = invoiceData?.current_approval?.status === "approved";
  const isRejected = invoiceData?.current_approval?.status === "rejected";
  const isActedUpon = isApproved || isRejected;

  return (
    <div className="trip_cost_estimation_page invoice_details_page">
      <Breadcrumbs values={["Finance", "Invoices", "Invoice Details"]} />

      <div className="page_header access_view">
        <h2>Invoice Details</h2>

        <div className="d-flex align-items-center gap-2 invoice_detail_header_actions">
          <Button
            cancelBtn={() => navigate("/invoices")}
            shouldRow={false}
            isShowSubmitBtn={false}
            cancelBtnText="Back"
          />
          <Button
            iconButton={true}
            type="button"
            iconBtnText="Export PDF"
            icon={<LuDownload />}
            shouldRow={false}
            onClick={handleDownloadInvoice}
            loading={downloading}
            loadingText="Exporting..."
          />
          {invoiceData?.current_approval && (
            <>
              <button
                type="button"
                className="cancel_btn"
                style={{
                  backgroundColor: isApproved
                    ? "#006b44"
                    : isRejected
                      ? "#f3f4f6"
                      : "#006b44",
                  color: isRejected ? "#9ca3af" : "#ffffff",
                  borderColor: isApproved
                    ? "#006b44"
                    : isRejected
                      ? "#e5e7eb"
                      : "#006b44",
                  fontWeight: 500,
                  fontSize: "14px",
                  padding: "5px 16px",
                  borderRadius: "8px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isActedUpon ? "not-allowed" : "pointer",
                }}
                onClick={() =>
                  !isActedUpon && handleOpenApprovalModal("approve")
                }
                disabled={isActedUpon}
              >
                {isApproved ? "Approved" : "Approve"}
              </button>
              <button
                type="button"
                className="cancel_btn"
                style={{
                  backgroundColor: isRejected
                    ? "#dc2626"
                    : isApproved
                      ? "#f3f4f6"
                      : "#dc2626",
                  color: isApproved ? "#9ca3af" : "#ffffff",
                  borderColor: isRejected
                    ? "#dc2626"
                    : isApproved
                      ? "#e5e7eb"
                      : "#dc2626",
                  fontWeight: 500,
                  fontSize: "14px",
                  padding: "5px 16px",
                  borderRadius: "8px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isActedUpon ? "not-allowed" : "pointer",
                }}
                onClick={() =>
                  !isActedUpon && handleOpenApprovalModal("reject")
                }
                disabled={isActedUpon}
              >
                {isRejected ? "Rejected" : "Reject"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="page_container">
        <Row className="driver_page_layout trip_cost_estimation_details_layout invoice_details_layout">
          <Col lg={9}>
            {invoiceData?.current_approval && (
              <div
                className="invoice_approval_banner"
                style={{
                  backgroundColor: isApproved
                    ? "#e6f9ed"
                    : isRejected
                      ? "#fef2f2"
                      : "#e6f9ed",
                  border: isApproved
                    ? "1px solid #bbf7d0"
                    : isRejected
                      ? "1px solid #fecaca"
                      : "1px solid #bbf7d0",
                  color: isApproved
                    ? "#15803d"
                    : isRejected
                      ? "#b91c1c"
                      : "#15803d",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  fontSize: "14px",
                }}
              ></div>
            )}

            <div className="invoice_detail_bar d-flex align-items-center justify-content-between">
              <div
                className="d-flex align-items-center"
                style={{ gap: "24px" }}
              >
                <div>
                  <span>Invoice ID:</span>
                  <strong>{overviewValues.invoiceId}</strong>
                </div>
                <div
                  className="d-flex align-items-center"
                  style={{ gap: "8px" }}
                >
                  <span>Status:</span>
                  <span
                    className={`invoice_status is_${String(invoiceData?.status || "").toLowerCase()}`}
                    style={{ textTransform: "capitalize" }}
                  >
                    <span>{formatStatusLabel(invoiceData?.status)}</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="driver_payment_tab_row invoice_details_tab_row">
              {DETAIL_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={activeTab === tab.key ? "is_active" : ""}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* TAB CONTENT */}
            {activeTab === "general_details" && (
              <div className="trip_information_main_div_invoice driver_form_shell trip_cost_estimation_detail_shell invoice_detail_shell">
                <div className="driver_form_content">
                  {(() => {
                    const hasRental =
                      Boolean(invoiceData?.include_monthly_rental) ||
                      rentalBreakdownRows.length > 0;
                    const financePanelWidth = hasRental ? 4 : 6;

                    return (
                      <>
                        <Row className="g-3">
                          <Col md={6}>
                            <div className="invoice_overview_card">
                              <h4>Bill To</h4>
                              <div className="invoice_info_grid">
                                <div className="invoice_info_field">
                                  <span className="invoice_info_label">
                                    Customer
                                  </span>
                                  <span className="invoice_info_value">
                                    {overviewValues.customerName}
                                  </span>
                                </div>
                                {invoiceData?.customer?.contact && (
                                  <div className="invoice_info_field">
                                    <span className="invoice_info_label">
                                      Contact
                                    </span>
                                    <span className="invoice_info_value">
                                      {invoiceData.customer.contact}
                                    </span>
                                  </div>
                                )}
                                {invoiceData?.customer?.email && (
                                  <div className="invoice_info_field">
                                    <span className="invoice_info_label">
                                      Email
                                    </span>
                                    <span className="invoice_info_value">
                                      {invoiceData.customer.email}
                                    </span>
                                  </div>
                                )}
                                <div className="invoice_info_field">
                                  <span className="invoice_info_label">
                                    NTN
                                  </span>
                                  <span className="invoice_info_value">
                                    {invoiceData?.customer?.ntn_number ||
                                      invoiceData?.customer?.ntn ||
                                      "-"}
                                  </span>
                                </div>
                                <div className="invoice_info_field">
                                  <span className="invoice_info_label">
                                    SNTN
                                  </span>
                                  <span className="invoice_info_value">
                                    {invoiceData?.customer?.sntn_number ||
                                      invoiceData?.customer?.sntn ||
                                      "-"}
                                  </span>
                                </div>
                                <div className="invoice_info_field is_full">
                                  <span className="invoice_info_label">
                                    Address
                                  </span>
                                  <span className="invoice_info_value">
                                    {invoiceData?.customer?.address || "-"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Col>

                          <Col md={6}>
                            <div className="invoice_overview_card">
                              <h4>Invoice Info</h4>
                              <div className="invoice_info_grid">
                                <div className="invoice_info_field">
                                  <span className="invoice_info_label">
                                    Contract
                                  </span>
                                  <span className="invoice_info_value">
                                    {overviewValues.contractName}
                                  </span>
                                </div>
                                <div className="invoice_info_field">
                                  <span className="invoice_info_label">
                                    Invoice Type
                                  </span>
                                  <span className="invoice_info_value">
                                    {overviewValues.invoiceType}
                                  </span>
                                </div>
                                <div className="invoice_info_field">
                                  <span className="invoice_info_label">
                                    Trips
                                  </span>
                                  <span className="invoice_info_value">
                                    {invoiceData?.trips_count ?? 0}
                                  </span>
                                </div>
                                {invoiceData?.created_by_user?.username && (
                                  <div className="invoice_info_field">
                                    <span className="invoice_info_label">
                                      Created By
                                    </span>
                                    <span className="invoice_info_value">
                                      {invoiceData.created_by_user.username}
                                    </span>
                                  </div>
                                )}
                                {invoiceData?.billing_period_from && (
                                  <div className="invoice_info_field is_full">
                                    <span className="invoice_info_label">
                                      Billing Period
                                    </span>
                                    <span className="invoice_info_value">
                                      {formatInputDate(
                                        invoiceData.billing_period_from,
                                      )}{" "}
                                      to{" "}
                                      {formatInputDate(
                                        invoiceData.billing_period_to,
                                      )}
                                    </span>
                                  </div>
                                )}
                                {invoiceData?.invoice_at && (
                                  <div className="invoice_info_field">
                                    <span className="invoice_info_label">
                                      Invoice Date
                                    </span>
                                    <span className="invoice_info_value">
                                      {formatInputDate(invoiceData.invoice_at)}
                                    </span>
                                  </div>
                                )}
                                {invoiceData?.invoice_due_at && (
                                  <div className="invoice_info_field">
                                    <span className="invoice_info_label">
                                      Due Date
                                    </span>
                                    <span className="invoice_info_value">
                                      {formatInputDate(
                                        invoiceData.invoice_due_at,
                                      )}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>
                        </Row>

                        <Row className="g-3 mt-1">
                          <Col md={financePanelWidth}>
                            <div className="invoice_finance_panel">
                              <h5 className="invoice_finance_panel_title">
                                Trip Revenue
                              </h5>
                              <div className="invoice_finance_row">
                                <span>Gross</span>
                                <span>
                                  PKR{" "}
                                  {formatAmount(
                                    invoiceData?.trip_revenue_amount ??
                                      invoiceData?.gross_amount,
                                  )}
                                </span>
                              </div>
                              <div className="invoice_finance_row">
                                <span>Deduction</span>
                                <span>
                                  - PKR{" "}
                                  {formatAmount(
                                    invoiceData?.deduction_amount,
                                  )}
                                </span>
                              </div>
                              <div className="invoice_finance_row">
                                <span>Tax</span>
                                <span>
                                  PKR {formatAmount(invoiceData?.tax_amount)}
                                </span>
                              </div>
                              <div className="invoice_finance_row is_subtotal">
                                <span>Trip Net</span>
                                <span>
                                  PKR {formatAmount(invoiceData?.net_amount)}
                                </span>
                              </div>
                            </div>
                          </Col>

                          {hasRental && (
                            <Col md={financePanelWidth}>
                              <div className="invoice_finance_panel">
                                <h5 className="invoice_finance_panel_title">
                                  Monthly Rental
                                </h5>
                                <div className="invoice_finance_row">
                                  <span>Gross</span>
                                  <span>
                                    PKR{" "}
                                    {formatAmount(
                                      invoiceData?.rental_gross_amount,
                                    )}
                                  </span>
                                </div>
                                <div
                                  className="invoice_finance_row"
                                  style={{ visibility: "hidden" }}
                                  aria-hidden="true"
                                >
                                  <span>Spacer</span>
                                  <span>PKR 0</span>
                                </div>
                                <div className="invoice_finance_row">
                                  <span>
                                    Tax (
                                    {formatAmount(
                                      invoiceData?.rental_tax_percentage,
                                    )}
                                    %)
                                  </span>
                                  <span>
                                    PKR{" "}
                                    {formatAmount(
                                      invoiceData?.rental_tax_amount,
                                    )}
                                  </span>
                                </div>
                                <div className="invoice_finance_row is_subtotal">
                                  <span>Rental Net</span>
                                  <span>
                                    PKR{" "}
                                    {formatAmount(
                                      invoiceData?.rental_net_amount,
                                    )}
                                  </span>
                                </div>
                              </div>
                            </Col>
                          )}

                          <Col md={financePanelWidth}>
                            <div className="invoice_finance_panel is_highlight">
                              <h5 className="invoice_finance_panel_title">
                                Total Invoice
                              </h5>
                              <div className="invoice_finance_row">
                                <span>Overall Gross</span>
                                <span>
                                  PKR{" "}
                                  {formatAmount(
                                    invoiceData?.overall_gross_amount ??
                                      invoiceData?.gross_amount,
                                  )}
                                </span>
                              </div>
                              <div
                                className="invoice_finance_row"
                                style={{ visibility: "hidden" }}
                                aria-hidden="true"
                              >
                                <span>Spacer</span>
                                <span>PKR 0</span>
                              </div>
                              <div className="invoice_finance_row">
                                <span>Overall Tax</span>
                                <span>
                                  PKR{" "}
                                  {formatAmount(
                                    invoiceData?.overall_tax_amount ??
                                      invoiceData?.tax_amount,
                                  )}
                                </span>
                              </div>
                              <div className="invoice_finance_row is_grand_total">
                                <span>Total Amount</span>
                                <span>
                                  PKR {formatAmount(totalAmountVal)}
                                </span>
                              </div>
                            </div>
                          </Col>
                        </Row>

                        <Row className="g-3 mt-1">
                          <Col md={12}>
                            <div className="invoice_overview_card">
                              <h4>Receivable Status</h4>
                              <div className="invoice_receivable_stats_grid">
                                <div className="invoice_receivable_stat">
                                  <span className="invoice_receivable_stat_label">
                                    Received
                                  </span>
                                  <span
                                    className="invoice_receivable_stat_value"
                                    style={{ color: "#16a34a" }}
                                  >
                                    PKR {formatAmount(receivedVal)}
                                  </span>
                                </div>
                                <div className="invoice_receivable_stat">
                                  <span className="invoice_receivable_stat_label">
                                    Withheld
                                  </span>
                                  <span className="invoice_receivable_stat_value">
                                    PKR {formatAmount(withheldVal)}
                                  </span>
                                </div>
                                <div className="invoice_receivable_stat is_highlight">
                                  <span
                                    className="invoice_receivable_stat_label"
                                    style={{ color: "#2563eb" }}
                                  >
                                    Outstanding (AR)
                                  </span>
                                  <span className="invoice_outstanding_highlight">
                                    PKR {formatAmount(outstandingVal)}
                                  </span>
                                </div>
                              </div>

                              <div className="invoice_receivable_desc">
                                AR is created only when the invoice is
                                Issued. Revenue posts to P&L only when
                                payment is recorded (cash basis).
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </>
                    );
                  })()}

                  {/* <div className="invoice_payment_note_bar">
                    Payment should be made in favor of <strong>J HOLDINGS (PRIVATE) LIMITED</strong> through crossed cheque, banker's cheque or IBFT to Company's IBAN: <strong>PK00JHCO...</strong>
                  </div> */}

                  {/* Other Tables section with headings */}
                  <h3 className="invoice_section_title mt-5 mb-3">
                    Trips & Calculation
                  </h3>
                  <NewTableComponent
                    columns={tripsCalculationColumns}
                    data={tripsCalculationRows}
                    showFilter={false}
                    showPagination={false}
                    tableClassName="invoice_details_tab_table mb-4"
                  />

                  {rentalBreakdownRows.length > 0 && (
                    <>
                      <h3 className="invoice_section_title mt-5 mb-3">
                        Monthly Rental Breakdown
                      </h3>
                      <NewTableComponent
                        columns={rentalBreakdownColumns}
                        data={rentalBreakdownRows}
                        showFilter={false}
                        showPagination={false}
                        tableClassName="invoice_details_tab_table mb-4"
                      />
                    </>
                  )}

                  <h3 className="invoice_section_title mt-5 mb-3">
                    Approval History
                  </h3>
                  <NewTableComponent
                    columns={approvalHistoryColumns}
                    data={approvalHistoryRows}
                    showFilter={false}
                    showPagination={false}
                    tableClassName="invoice_details_tab_table mb-4"
                  />

                </div>
              </div>
            )}

            {activeTab === "payment_history" && (
              <div className="trip_information_main_div driver_form_shell trip_cost_estimation_detail_shell invoice_detail_shell">
                <div className="driver_form_content">
                  <div
                    className="content_box"
                    style={{
                      background: "#ffffff",
                      padding: "24px",
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      marginTop: "0px",
                    }}
                  >
                    <h6
                      style={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#1e293b",
                        marginBottom: "4px",
                      }}
                    >
                      Payment Ledger Summary
                    </h6>
                    <p
                      style={{
                        fontSize: "13px",
                        color: "#64748b",
                        marginBottom: "20px",
                      }}
                    >
                      High-level overview of the financial health and
                      obligations of the Invoice
                    </p>

                    <Row className="mb-4">
                      <Col md={4}>
                        <Input
                          label="Total Payable"
                          placeholder="00"
                          extraText="PKR"
                          showExtraText={true}
                          required={true}
                          disabled={true}
                          value={formatAmount(totalAmountVal)}
                        />
                      </Col>
                      <Col md={4}>
                        <Input
                          label="Amount Paid"
                          placeholder="00"
                          extraText="PKR"
                          showExtraText={true}
                          required={true}
                          value={formatAmount(receivedVal)}
                          disabled={true}
                        />
                      </Col>
                      <Col md={4}>
                        <Input
                          label="Outstanding Amount"
                          placeholder="00"
                          extraText="PKR"
                          showExtraText={true}
                          required={true}
                          value={formatAmount(outstandingVal)}
                          disabled={true}
                        />
                      </Col>
                    </Row>

                    <div className="finance_table_wrapper mt-4">
                      <table
                        className="w-100 finance_table"
                        style={{ borderCollapse: "collapse" }}
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                color: "#888EA8",
                                fontWeight: 500,
                                fontSize: "13px",
                                padding: "10px",
                                textAlign: "left",
                                borderBottom: "1px solid #E0E6ED",
                              }}
                            >
                              Transaction ID
                            </th>
                            <th
                              style={{
                                color: "#888EA8",
                                fontWeight: 500,
                                fontSize: "13px",
                                padding: "10px",
                                textAlign: "left",
                                borderBottom: "1px solid #E0E6ED",
                              }}
                            >
                              Amount Paid (PKR)
                            </th>
                            <th
                              style={{
                                color: "#888EA8",
                                fontWeight: 500,
                                fontSize: "13px",
                                padding: "10px",
                                textAlign: "left",
                                borderBottom: "1px solid #E0E6ED",
                              }}
                            >
                              Payment Method
                            </th>
                            <th
                              style={{
                                color: "#888EA8",
                                fontWeight: 500,
                                fontSize: "13px",
                                padding: "10px",
                                textAlign: "left",
                                borderBottom: "1px solid #E0E6ED",
                              }}
                            >
                              Reference Number
                            </th>
                            <th
                              style={{
                                color: "#888EA8",
                                fontWeight: 500,
                                fontSize: "13px",
                                padding: "10px",
                                textAlign: "right",
                                borderBottom: "1px solid #E0E6ED",
                              }}
                            >
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentHistoryRows.length ? (
                            paymentHistoryRows.map((transaction) => {
                              const transactionLabel = `TR-${transaction?.id ?? "-"}`;
                              const methodStr = String(
                                transaction?.method || "",
                              ).toLowerCase();
                              const displayMethod =
                                methodStr === "bank_transfer"
                                  ? "Bank Transfer"
                                  : methodStr.charAt(0).toUpperCase() +
                                    methodStr.slice(1);

                              return (
                                <tr key={transaction?.id ?? transactionLabel}>
                                  <td
                                    style={{
                                      padding: "12px 10px",
                                      fontSize: "13px",
                                      color: "#3D3F5C",
                                      borderBottom: "1px solid #E0E6ED",
                                      textAlign: "left",
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: "#4F19E2",
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        fontWeight: 500,
                                      }}
                                      onClick={() =>
                                        handleOpenEditTransactionModal(
                                          transaction,
                                        )
                                      }
                                    >
                                      {transactionLabel}
                                    </span>
                                  </td>
                                  <td
                                    style={{
                                      padding: "12px 10px",
                                      fontSize: "13px",
                                      color: "#3D3F5C",
                                      borderBottom: "1px solid #E0E6ED",
                                      textAlign: "left",
                                    }}
                                  >
                                    {formatAmount(transaction?.amount)}
                                  </td>
                                  <td
                                    style={{
                                      padding: "12px 10px",
                                      fontSize: "13px",
                                      color: "#3D3F5C",
                                      borderBottom: "1px solid #E0E6ED",
                                      textAlign: "left",
                                    }}
                                  >
                                    {displayMethod}
                                  </td>
                                  <td
                                    style={{
                                      padding: "12px 10px",
                                      fontSize: "13px",
                                      color: "#3D3F5C",
                                      borderBottom: "1px solid #E0E6ED",
                                      textAlign: "left",
                                    }}
                                  >
                                    {transaction?.refNo || "-"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "12px 10px",
                                      fontSize: "13px",
                                      color: "#3D3F5C",
                                      borderBottom: "1px solid #E0E6ED",
                                      textAlign: "right",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                        gap: "12px",
                                      }}
                                    >
                                      <FiEdit
                                        size={16}
                                        style={{
                                          cursor: "pointer",
                                          color: "#1A4FBA",
                                        }}
                                        onClick={() =>
                                          handleOpenEditTransactionModal(
                                            transaction,
                                          )
                                        }
                                      />
                                      <FiTrash2
                                        size={16}
                                        style={{
                                          cursor: "pointer",
                                          color: "#DC2626",
                                        }}
                                        onClick={() =>
                                          handleOpenDeleteTransactionModal(
                                            transaction,
                                          )
                                        }
                                      />
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td
                                colSpan={5}
                                style={{
                                  padding: "16px 10px",
                                  textAlign: "center",
                                  color: "#888EA8",
                                  fontSize: "13px",
                                }}
                              >
                                No payment transactions found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <button
                      type="button"
                      className="add_transaction_btn"
                      style={{
                        border: "1px solid #E2E3E8",
                        borderRadius: "6px",
                        padding: "10px",
                        textAlign: "center",
                        marginTop: "15px",
                        width: "100%",
                        color: "#3D3F5C",
                        fontSize: "13px",
                        background: "#fff",
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                      onClick={handleOpenAddTransactionModal}
                    >
                      + Add Transaction
                    </button>

                    <CustomModal
                      show={showAddTransactionModal}
                      handleClose={() => setShowAddTransactionModal(false)}
                      heading={
                        isEditingPaymentTransaction
                          ? "Edit Transaction"
                          : "Add Transaction"
                      }
                    >
                      <Formik
                        enableReinitialize={true}
                        initialValues={paymentTransactionModalInitialValues}
                        validate={(values) => {
                          const errors = {};
                          if (!values.amount) {
                            errors.amount = "Amount is required";
                          } else if (Number(values.amount) <= 0) {
                            errors.amount = "Amount must be greater than 0";
                          }
                          if (!values.payment_method) {
                            errors.payment_method =
                              "Payment method is required";
                          }
                          if (values.payment_method === "bank_transfer") {
                            if (!values.bank_name) {
                              errors.bank_name = "Bank name is required";
                            }
                            if (!values.account_title) {
                              errors.account_title =
                                "Account title is required";
                            }
                            if (!values.iban_number) {
                              errors.iban_number = "IBAN number is required";
                            }
                          }
                          if (
                            !values.fileObjects ||
                            values.fileObjects.length === 0
                          ) {
                            errors.fileObjects = "Attachment is required";
                          }
                          return errors;
                        }}
                        onSubmit={
                          isEditingPaymentTransaction
                            ? handleEditPaymentTransaction
                            : handleAddPaymentTransaction
                        }
                      >
                        {({
                          values,
                          errors,
                          touched,
                          handleChange,
                          handleBlur,
                          setFieldValue,
                          setFieldTouched,
                          handleSubmit,
                          isSubmitting,
                        }) => {
                          const modalAttachmentFiles = Array.isArray(
                            values.fileObjects,
                          )
                            ? values.fileObjects
                            : [];
                          const transactionModalLabel =
                            isEditingPaymentTransaction
                              ? `TR-${editingTransaction?.id ?? "-"}`
                              : `TR-${paymentsData.length + 1}`;
                          return (
                            <form onSubmit={handleSubmit} className="px-1">
                              <Row className="mb-3">
                                <Col md={12}>
                                  <div
                                    style={{
                                      background: "#F9F9FB",
                                      padding: "10px 15px",
                                      borderRadius: "6px",
                                      color: "#3D3F5C",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "5px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: "#888EA8",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Transaction ID:
                                    </span>
                                    <span
                                      style={{
                                        color: "#4F19E2",
                                        fontWeight: 500,
                                        fontSize: "13px",
                                      }}
                                    >
                                      {transactionModalLabel}
                                    </span>
                                  </div>
                                </Col>
                              </Row>

                              <Row className="mb-2">
                                <Col md={12}>
                                  <Input
                                    label="Amount"
                                    required={true}
                                    name="amount"
                                    type="number"
                                    placeholder="00"
                                    extraText="PKR"
                                    showExtraText={true}
                                    value={values.amount}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.amount}
                                    touched={touched.amount}
                                  />
                                </Col>
                              </Row>

                              <Row className="mb-2">
                                <Col md={12}>
                                  <Input
                                    label="Reference Number"
                                    required={false}
                                    name="reference_number"
                                    type="text"
                                    placeholder="TRX-123"
                                    value={values.reference_number}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.reference_number}
                                    touched={touched.reference_number}
                                  />
                                </Col>
                              </Row>

                              <Row className="mb-2">
                                <Col md={12}>
                                  <Select
                                    label="Payment Method"
                                    placeholder="Select Payment Method"
                                    options={paymentMethodOptions}
                                    value={
                                      paymentMethodOptions.find(
                                        (opt) =>
                                          opt.value === values.payment_method,
                                      ) || null
                                    }
                                    onChange={(opt) => {
                                      const method = opt?.value || "";
                                      setFieldValue("payment_method", method);
                                      if (method !== "bank_transfer") {
                                        setFieldValue("bank_name", "");
                                        setFieldValue("account_title", "");
                                        setFieldValue("iban_number", "");
                                      }
                                    }}
                                    onBlur={handleBlur}
                                    error={errors.payment_method}
                                    touched={touched.payment_method}
                                  />
                                </Col>
                              </Row>

                              {values.payment_method === "bank_transfer" && (
                                <Row className="mb-2">
                                  <Col md={6}>
                                    <Select
                                      label="Bank"
                                      placeholder="Select Bank"
                                      options={banksDropdownData}
                                      value={
                                        banksDropdownData.find(
                                          (opt) =>
                                            opt.value === values.bank_name ||
                                            opt.label === values.bank_name,
                                        ) || null
                                      }
                                      onChange={(opt) =>
                                        setFieldValue(
                                          "bank_name",
                                          opt?.label || "",
                                        )
                                      }
                                      onBlur={handleBlur}
                                      error={errors.bank_name}
                                      touched={touched.bank_name}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="Account Title"
                                      name="account_title"
                                      type="text"
                                      placeholder="Enter Account Title"
                                      value={values.account_title}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.account_title}
                                      touched={touched.account_title}
                                    />
                                  </Col>
                                  <Col md={12} className="mt-2">
                                    <Input
                                      label="IBAN Number"
                                      name="iban_number"
                                      type="text"
                                      placeholder="PK00XXXX"
                                      value={values.iban_number}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.iban_number}
                                      touched={touched.iban_number}
                                    />
                                  </Col>
                                </Row>
                              )}

                              <Row className="mb-2">
                                <Col md={12}>
                                  <Input
                                    label="Notes"
                                    required={false}
                                    name="notes"
                                    type="text"
                                    placeholder="Payment details..."
                                    value={values.notes}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.notes}
                                    touched={touched.notes}
                                  />
                                </Col>
                              </Row>

                              <Row className="mb-3">
                                <Col md={12}>
                                  <label
                                    style={{
                                      display: "block",
                                      fontSize: "13px",
                                      color: "#3D3F5C",
                                      marginBottom: "6px",
                                    }}
                                  >
                                    Attachments{" "}
                                    <span style={{ color: "#FA6262" }}>*</span>
                                  </label>
                                  <FileUpload
                                    files={modalAttachmentFiles}
                                    setFiles={(updater) => {
                                      const baseFiles =
                                        modalAttachmentFiles || [];
                                      const nextFiles =
                                        typeof updater === "function"
                                          ? updater(baseFiles)
                                          : updater;
                                      setFieldValue("fileObjects", nextFiles);
                                      setFieldValue(
                                        "files",
                                        (nextFiles || [])
                                          .map(
                                            (file) =>
                                              file?.fileName || file?.name,
                                          )
                                          .filter(Boolean),
                                      );
                                      setFieldTouched(
                                        "fileObjects",
                                        true,
                                        false,
                                      );
                                    }}
                                    maxFileSize={10}
                                    destination="invoices"
                                    consumeUploadedFiles={
                                      showAddTransactionModal
                                    }
                                  />
                                  {touched.fileObjects &&
                                    errors.fileObjects && (
                                      <div
                                        style={{
                                          color: "#FA6262",
                                          fontSize: "12px",
                                          marginTop: "4px",
                                        }}
                                      >
                                        {errors.fileObjects}
                                      </div>
                                    )}
                                </Col>
                              </Row>

                              <Row>
                                <Col md={6}>
                                  <button
                                    type="button"
                                    className="w-100"
                                    style={{
                                      background: "white",
                                      border: "1px solid #4F19E2",
                                      color: "#4F19E2",
                                      borderRadius: "6px",
                                      height: "40px",
                                      fontWeight: 500,
                                    }}
                                    onClick={() =>
                                      setShowAddTransactionModal(false)
                                    }
                                  >
                                    Cancel
                                  </button>
                                </Col>
                                <Col md={6}>
                                  <button
                                    type="submit"
                                    className="w-100"
                                    disabled={isSubmitting || transactionSaving}
                                    style={{
                                      background: "#4F19E2",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "6px",
                                      height: "40px",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {isSubmitting || transactionSaving
                                      ? "Saving..."
                                      : isEditingPaymentTransaction
                                        ? "Save Changes"
                                        : "Record Payment"}
                                  </button>
                                </Col>
                              </Row>
                            </form>
                          );
                        }}
                      </Formik>
                    </CustomModal>
                  </div>
                </div>
              </div>
            )}
          </Col>

          <Col lg={3}>
            <div className="service_cost_summary_details driver_details_sidebar trip_cost_estimation_details_sidebar invoice_detail_sidebar">
              <LogsComments
                key={id || "invoice-history"}
                historyItems={historyItems}
                onFetchHistory={handleFetchInvoiceHistory}
                onSubmitComment={handleSubmitInvoiceComment}
                loading={historyLoading || commentSubmitting}
                editorModules={modules}
                editorFormats={formats}
                emptyMessages={{
                  all: "No comments or activity found",
                  comment: "No comments found",
                  logs: "No activity found",
                }}
              />

              <div className="driver_payment_notes_card driver_details_notes_card invoice_detail_notes_card">
                <div className="parts_general_notes">
                  <h5>General Notes</h5>
                  <p>Saved invoice notes.</p>
                  <ReactQuill
                    placeholder="write notes here..."
                    modules={{ toolbar: false }}
                    theme="snow"
                    value={overviewValues.notes}
                    formats={formats}
                    readOnly
                    className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                  />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <ConfirmDeleteModal
        show={showDeleteTransactionModal}
        onClose={() => setShowDeleteTransactionModal(false)}
        onConfirm={handleDeletePaymentTransaction}
        loading={transactionSaving}
        title="Delete Transaction?"
        description={
          transactionToDelete?.id
            ? `This will permanently delete TR-${transactionToDelete.id}.`
            : "This action cannot be undone."
        }
        confirmText="Delete"
      />

      <CustomModal
        show={showApprovalModal}
        handleClose={() => setShowApprovalModal(false)}
        heading={
          approvalAction === "approve" ? "Approve Invoice" : "Reject Invoice"
        }
        size="md"
      >
        <div className="p-3">
          <p
            style={{ fontSize: "14px", color: "#6b7280", marginBottom: "16px" }}
          >
            {approvalAction === "approve"
              ? "Are you sure you want to approve this invoice?"
              : "Are you sure you want to reject this invoice?"}
          </p>

          <div className="mb-4">
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 500,
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Remarks / Comments
            </label>
            <textarea
              className="form-control"
              style={{
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
                padding: "10px",
                minHeight: "100px",
                resize: "vertical",
              }}
              placeholder="Type comments here (optional)..."
              value={approvalRemarks}
              onChange={(e) => setApprovalRemarks(e.target.value)}
            />
          </div>

          <Button
            cancelBtn={() => setShowApprovalModal(false)}
            submitBtn={handleApprovalActionSubmit}
            submitText="Confirm"
            cancelBtnText="Cancel"
            loading={approvalSubmitting}
            disabled={approvalSubmitting}
            buttonExtraCss={
              approvalAction === "approve"
                ? "approve_modal_btn"
                : "reject_modal_btn"
            }
            shouldRow={true}
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default InvoiceDetails;
